import {
  alpha,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ReplyIcon from '@material-ui/icons/Reply';
import { useSnackbar } from 'notistack';
import ReactPlayer, { ReactPlayerProps } from 'react-player';

import * as emitterEvents from 'constants/emitterEvents';
import * as socketEvents from 'constants/socketEvents';
import { useSearchQuery } from 'contexts/SearchQueryContext';
import Emitter from 'services/eventEmitter';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  MessageSocketError,
} from 'store/reducers/atendimentos';
import {
  addMensagemAtendimento,
  MensagemInterface,
  MensagemRespostaInterativaInterface,
  mensagensSelector,
  setBotoesInterativosDesabilitados,
  TiposDeCabecalhoMensagemInterativaEnum,
} from 'store/reducers/mensagens';
import { getFileNameAndExtension } from 'utils/getFileNameAndExtension';
import parseTextToHtml from 'utils/parseTextToHtml';

import ImageViewer from './ImageViewer';
import MessageBase, { MessageBaseProps } from './MessageBase';
import MessageErrorFallback from './MessageErrorFallback';
import ReadMoreParsedText from './ReadMoreParsedText';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    minWidth: spacing(40),
  },
  image: {
    width: '100%',
    borderRadius: spacing(1),
  },
  divider: {
    backgroundColor: palette.text.secondary,
    marginLeft: -16,
    marginRight: -16,
  },

  dividerReply: {
    backgroundColor: palette.text.secondary,
    marginLeft: -16,
    marginRight: -16,
  },

  document: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    minWidth: spacing(40),
    paddingBottom: spacing(1),
  },

  listItem: {
    color: 'inherit',
    borderRadius: spacing(1),
  },

  titulo: {
    overflow: 'hidden',
    width: spacing(24),
  },

  iconButton: {
    color: palette.getContrastText(alpha(palette.common.black, 0.4)),
    backgroundColor: `${alpha(palette.common.black, 0.4)} !important`,
  },

  messageText: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },

  readMoreButton: {
    paddingBlock: 0,
    minWidth: 'fit-content',
    textTransform: 'none',
    alignSelf: 'flex-start',
    color: palette.text.secondary,
  },

  headerText: {
    fontWeight: 600,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },

  footerText: {
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  replyButton: {
    color: 'inherit',
    '&:disabled': {
      color: palette.text.disabled,
    },
  },
}));

type ReplyButtonsMessageProps = MessageBaseProps;

function ReplyButtonsMessage(props: ReplyButtonsMessageProps) {
  const classes = useStyles();
  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);
  const { botoesInterativosDesabilitados } = useAppSelector(mensagensSelector);
  const searchQuery = useSearchQuery();

  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  try {
    const { mensagem } = props;
    const { whatsappInteractiveComponents } = mensagem;

    const { header, body, footer, action } =
      whatsappInteractiveComponents as MensagemRespostaInterativaInterface;

    const renderHeader = (type: TiposDeCabecalhoMensagemInterativaEnum) => {
      switch (type) {
        case TiposDeCabecalhoMensagemInterativaEnum.TEXTO:
          return (
            <Typography
              variant="subtitle2"
              className={classes.headerText}
              dangerouslySetInnerHTML={{
                __html: parseTextToHtml(header?.text || '', searchQuery),
              }}
            />
          );
        case TiposDeCabecalhoMensagemInterativaEnum.IMAGEM:
          return (
            <Box className={classes.image}>
              <ImageViewer src={header?.image?.link}>
                <img
                  src={header?.image?.link}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
              </ImageViewer>
            </Box>
          );
        case TiposDeCabecalhoMensagemInterativaEnum.DOCUMENTO: {
          if (!header?.document) return null;
          const { name, extension } = getFileNameAndExtension(
            header.document.link,
          );
          return (
            <Box className={classes.document}>
              <ListItem
                className={classes.listItem}
                title="Baixar documento"
                component={Link}
                target="_blank"
                rel="noopener noreferrer"
                href={header.document.link}
                disableGutters
              >
                <ListItemAvatar>
                  <InsertDriveFileIcon fontSize="large" color="inherit" />
                </ListItemAvatar>
                <Typography variant="body2" className={classes.titulo}>
                  {`${name}${extension ? `.${extension}` : ''}`}
                </Typography>
              </ListItem>
            </Box>
          );
        }
        case TiposDeCabecalhoMensagemInterativaEnum.VIDEO: {
          const playerOptions = {
            url: header?.video?.link || '',
            pip: true,
            controls: true,
            width: '100%',
            height: '100%',
            playIcon: (
              <IconButton className={classes.iconButton}>
                <PlayArrowIcon color="inherit" fontSize="large" />
              </IconButton>
            ),
          } as ReactPlayerProps;
          return <ReactPlayer {...playerOptions} />;
        }
        default:
          return null;
      }
    };

    const handleSendText = (texto: string, buttonId: string) => {
      if (atendimentoSelecionado?.id !== mensagem.atendimentoId) {
        return;
      }

      const atendimentoId = atendimentoSelecionado?.id;
      const mensagemCitadaId = mensagem.id;

      if (atendimentoId) {
        const data = { atendimentoId, mensagem: texto, mensagemCitadaId };
        socket.emit(
          socketEvents.EMIT_ENVIAR_MENSAGEM,
          data,
          (msgEnviada: MensagemInterface | MessageSocketError) => {
            if ('error' in msgEnviada) {
              enqueueSnackbar(msgEnviada.error);
              return;
            }

            dispatch(
              setBotoesInterativosDesabilitados(`${mensagem.id}-${buttonId}`),
            );
            dispatch(addMensagemAtendimento(msgEnviada));
            Emitter.emit(
              emitterEvents.ENVIAR_NOVA_MENSAGEM_ATENDENTE_NO_SCROLL_TOP,
              true,
            );
          },
        );
      }
    };

    return (
      <MessageBase {...props}>
        <Box padding={1}>
          {/* Header */}
          {header && header.type && <Box>{renderHeader(header.type)}</Box>}

          {/* Body and footer text */}
          <ListItemText
            className={classes.messageText}
            primary={
              <ReadMoreParsedText texto={body.text || ''} variant="body2" />
            }
            secondary={
              footer?.text ? (
                <Typography
                  variant="caption"
                  className={classes.footerText}
                  dangerouslySetInnerHTML={{
                    __html: parseTextToHtml(footer.text, searchQuery),
                  }}
                />
              ) : null
            }
          />
        </Box>

        {/* Reply buttons */}
        {action.buttons.map(button => {
          return (
            <Box key={`${button.reply.id}`}>
              <Divider className={classes.dividerReply} />

              <Box p={1}>
                <Button
                  className={classes.replyButton}
                  fullWidth
                  size="small"
                  startIcon={<ReplyIcon />}
                  disabled={
                    atendimentoSelecionado?.id === mensagem.atendimentoId &&
                    !!botoesInterativosDesabilitados[
                      `${mensagem.id}-${button.reply.id}`
                    ]
                  }
                  onClick={() =>
                    handleSendText(
                      `@sem-autor\n${button.reply.title}`,
                      button.reply.id,
                    )
                  }
                >
                  {button.reply.title}
                </Button>
              </Box>
            </Box>
          );
        })}
      </MessageBase>
    );
  } catch (error) {
    return <MessageErrorFallback {...props} />;
  }
}

export default ReplyButtonsMessage;
